import type { World } from './types.js';
import { headingFromDirection, pointAlong } from './geom.js';

export interface Spawn {
  x: number;
  z: number;
  heading: number;
}

/** Two cars abreast, four rows — the grid is sized for the 8-player cap. */
const GRID_ROWS = 4;
const ROW_SPACING = 7;
const FRONT_ROW_OFFSET = 4;
/** Half the lateral gap between the two cars in a row, metres. */
const LANE_OFFSET = 2.4;

/**
 * Where the car with a given index on the grid starts.
 *
 * Positions are sampled *along the route polyline* rather than offset backwards from the
 * start point. Offsetting backwards seemed natural but routinely put the back of the grid
 * on grass or inside a building — the start snaps to a road node, which is frequently the
 * end of a way, so there is often no road behind it at all. Walking forwards along the
 * route is guaranteed to stay on tarmac, and the twenty-odd metres it costs is nothing
 * against a checkpoint three hundred metres away.
 *
 * Shared by the server (which seeds the first snapshot) and the client (which places the
 * local car before the countdown ends). Both derive the index from the same join-ordered
 * player list, so nobody spawns inside anybody else and no extra message is needed.
 */
export function gridSpawn(route: World['route'], index: number): Spawn {
  const row = Math.floor(index / 2) % GRID_ROWS;
  const col = index % 2 === 0 ? -1 : 1;
  // Row 0 is pole, so it sits furthest along the route.
  const distance = FRONT_ROW_OFFSET + (GRID_ROWS - 1 - row) * ROW_SPACING;
  const point = pointAlong(route.path, Math.min(distance, route.lengthMetres * 0.25));

  // Perpendicular to the direction of travel, pointing to the driver's right.
  const rightX = -point.dz;
  const rightZ = point.dx;
  const side = col * LANE_OFFSET;

  return {
    x: point.x + rightX * side,
    z: point.z + rightZ * side,
    heading: headingFromDirection(point.dx, point.dz),
  };
}
